import { z } from "zod";

import { VibeUsageTypeReadSchema } from "../../../../shared/domain/vibe/usage-types";

/**
 * A UTC calendar day, `YYYY-MM-DD`. Same reasoning as the sibling usage read:
 * carried as a plain date string, never an ISO datetime, because a UTC-midnight
 * `Date` rendered west of UTC shows the previous calendar day.
 */
const YmdDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "date must be an ISO calendar day (YYYY-MM-DD)");

/** One day's consumption for a single usage type inside the cycle. */
export const VibeUsageCycleDailyPointSchema = z.object({
  date: YmdDateSchema,
  quantity: z.number().nonnegative()
});

export type VibeUsageCycleDailyPointDto = z.infer<typeof VibeUsageCycleDailyPointSchema>;

/**
 * One usage type's cycle-to-date consumption, its cap, and the daily series
 * that composes it. `total` is the sum of `daily` by construction — the
 * endpoint derives both from one aggregate, so a console that draws a
 * cumulative curve toward `effectiveCap` can never end the curve somewhere
 * other than the figure printed beside it.
 */
export const VibeUsageCycleMeterSchema = z.object({
  usageType: VibeUsageTypeReadSchema,
  /// Effective per-type cap for the cycle (org override ?? platform default).
  effectiveCap: z.number().int().nonnegative(),
  /// Cycle-to-date consumption. Equals the sum of `daily`.
  total: z.number().nonnegative(),
  /// Ascending by date; days with no consumption are omitted.
  daily: z.array(VibeUsageCycleDailyPointSchema)
});

export type VibeUsageCycleMeterDto = z.infer<typeof VibeUsageCycleMeterSchema>;

/**
 * One app's cycle-to-date consumption, per usage type.
 *
 * `vibeAppId` is NULLABLE and that null is a real row, not a gap: egress from
 * a non-app component (Aurora replica, Vault sync) carries no per-app
 * attribution, and a table that silently dropped those rows would print
 * per-app shares that do not add up to the org total. The console renders the
 * null row explicitly as org-wide.
 *
 * `name` survives a SOFT delete. Usage events keep their `vibeAppId` through
 * one (the FK only `SetNull`s on a hard delete) and the soft-delete adapter
 * deliberately leaves the row's real name intact, so an app removed mid-cycle
 * still reports its consumption under the name it went by. Name resolution
 * therefore reads a tombstone-inclusive port; going through the live-apps list
 * would label every one of them as deleted while the name sat in the table.
 *
 * `name` is null in exactly two cases: the org-wide row (`vibeAppId` null), and
 * an id whose app row is gone entirely — a hard delete, where the consumption
 * is still real and still counts toward the org total.
 */
export const VibeUsageCycleAppSchema = z.object({
  vibeAppId: z.string().uuid().nullable(),
  name: z.string().nullable(),
  totals: z.array(
    z.object({
      usageType: VibeUsageTypeReadSchema,
      quantity: z.number().nonnegative()
    })
  )
});

export type VibeUsageCycleAppDto = z.infer<typeof VibeUsageCycleAppSchema>;

/**
 * The billing-cycle usage read — everything the console needs to answer
 * "will I run out before the cycle ends, and which app is burning it".
 *
 * **One source, one window.** Every figure here is summed from `VibeUsageEvent`
 * over the current UTC calendar month, which is the same table and the same
 * window `GET /api/vibe/cost-safety` meters against. That is deliberate: the
 * sibling `GET /api/vibe/usage` reads the daily `VibeUsageRollup` over a
 * trailing 30-day window, and those two figures legitimately differ. Drawing a
 * cumulative curve toward a MONTHLY cap from a TRAILING-30-DAY series would
 * cross two cycles and mean nothing, so this read exists rather than
 * re-pointing the old one.
 *
 * `asOf` is load-bearing, not decoration: it names the current UTC day, which
 * is PARTIAL. A run rate computed over it — dividing a few hours of
 * consumption by a whole day — understates the trend, which is the difference
 * between a meter reading 95% and a projection that crosses the cap. Consumers
 * project from completed days only.
 */
export const GetVibeUsageCycleResponseSchema = z.object({
  /// UTC calendar month the cycle covers, `YYYY-MM`.
  billingPeriod: z.string().regex(/^\d{4}-\d{2}$/, "billingPeriod must be YYYY-MM"),
  /// First day of the cycle, inclusive.
  cycleStart: YmdDateSchema,
  /// Last day of the cycle, inclusive.
  cycleEnd: YmdDateSchema,
  /// The current UTC day — inside the cycle, and PARTIAL. See above.
  asOf: YmdDateSchema,
  /// One entry per `VibeUsageType` in canonical order — every type, even at zero.
  meters: z.array(VibeUsageCycleMeterSchema),
  /// One entry per app that consumed anything this cycle, plus at most one
  /// org-wide (`vibeAppId: null`) row. Apps with no consumption are absent.
  apps: z.array(VibeUsageCycleAppSchema)
});

export type GetVibeUsageCycleResponse = z.infer<typeof GetVibeUsageCycleResponseSchema>;
